<template>
  <button type="button" class="tour-trigger btn" @click="handleIniciarTourClick">
    <i class="ti ti-help-circle" aria-hidden="true"></i>
    <span>Ver tour</span>
  </button>
</template>

<script>
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { getUsuario } from '@/utils/authStorage';

export default {
  name: 'TourUsuario',
  emits: ['iniciar-tour'],

  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
    window.setTimeout(() => {
      if (this.$route.path.startsWith('/app/project/') || this.$route.path.startsWith('/app/calculator-')) {
        this.iniciarTourDaRotaAtual(this.$route.path);
      } else if (!this.tourFoiConcluido()) {
        this.iniciarTour();
      }
    }, 700);
  },

  watch: {
    '$route.path'(path) {
      if (path.startsWith('/app/project/')) {
        window.setTimeout(() => this.iniciarTourContextual('componentes'), 700);
      }

      if (path.startsWith('/app/calculator-')) {
        window.setTimeout(() => this.iniciarTourContextual('calculadora'), 700);
      }
    }
  },

  methods: {
    async handleIniciarTourClick() {
      this.$emit('iniciar-tour');

      if (this.$route.path.startsWith('/app/project/')) {
        this.iniciarTourContextual('componentes', true);
        return;
      }

      if (this.$route.path.startsWith('/app/calculator-')) {
        this.iniciarTourContextual('calculadora', true);
        return;
      }

      if (this.$route.path !== '/app') {
        await this.$router.push('/app');
        await this.$nextTick();
        window.setTimeout(() => this.iniciarTour(), 400);
        return;
      }

      this.iniciarTour();
    },

    chaveTour(contexto = 'navegacao') {
      const usuario = getUsuario();
      return `wisecalc-tour-${contexto}-concluido-${usuario?.email || 'usuario'}`;
    },

    tourFoiConcluido(contexto = 'navegacao') {
      return localStorage.getItem(this.chaveTour(contexto)) === 'true';
    },

    iniciarTour() {
      if (this.$route.path.startsWith('/app/project/')) {
        this.iniciarTourContextual('componentes', true);
        return;
      }

      if (this.$route.path.startsWith('/app/calculator-')) {
        this.iniciarTourContextual('calculadora', true);
        return;
      }

      const isDesktop = window.innerWidth >= 1200;

      this.executarTour([
        {
          element: isDesktop ? '.tour-logo' : '.tour-mobile-menu',
          popover: {
            title: 'Bem-vindo à WiseCalc',
            description: isDesktop
              ? 'Use este menu para navegar pelas principais áreas do sistema.'
              : 'Toque aqui para abrir o menu e acessar as áreas do sistema.'
          }
        },
        {
          element: '.tour-inicio-projetos',
          popover: {
            title: 'Projetos',
            description: 'Nesta seção você encontra, cria, abre e organiza seus projetos.'
          },
          onHighlightStarted: () => {
            if (!isDesktop) {
              document.querySelector('.tour-mobile-menu')?.click();
            }
          }
        },
        {
          element: '.tour-conta',
          popover: {
            title: 'Minha conta',
            description: 'Edite seus dados pessoais ou altere sua senha.'
          }
        },
        {
          element: '.tour-assinatura',
          popover: {
            title: 'Minha assinatura',
            description: 'Consulte seu plano e acompanhe o status da assinatura.'
          }
        },
        {
          element: '.tour-suporte',
          popover: {
            title: 'Suporte',
            description: 'Entre em contato quando precisar de ajuda.'
          }
        },
        {
          element: '.card-principal',
          popover: {
            title: 'Área de trabalho',
            description: 'Crie um novo projeto ou clique em um projeto existente para começar a trabalhar nele.'
          },
          onHighlightStarted: () => {
            if (!isDesktop) {
              document.querySelector('.left-sidebar .close-btn')?.click();
            }
          },
          onPrevClick: (element, step, options) => {
            if (isDesktop) {
              options.driver.movePrevious();
              return;
            }

            document.querySelector('.tour-mobile-menu')?.click();
            window.setTimeout(() => options.driver.movePrevious(), 300);
          }
        }
      ], 'navegacao');
    },

    handleDocumentClick(event) {
      if (!event.target.closest('.tour-novo-projeto')) return;

      window.setTimeout(() => this.iniciarTourContextual('criar-projeto'), 450);
    },

    iniciarTourContextual(contexto, forcar = false) {
      if (!forcar && this.tourFoiConcluido(contexto)) return;

      const stepsPorContexto = {
        'criar-projeto': [
          {
            element: '#adicionarProjetoModal .input-projeto',
            popover: {
              title: 'Dê um nome ao projeto',
              description: 'Digite um nome que facilite a identificação do seu projeto.'
            }
          },
          {
            element: '#adicionarProjetoModal .btn-primary',
            popover: {
              title: 'Crie o projeto',
              description: 'Depois de informar o nome, clique aqui para criar e abrir o projeto.'
            }
          }
        ],
        componentes: [
          {
            element: '.tour-projeto-cabecalho',
            popover: {
              title: 'Projeto aberto',
              description: 'Nesta tela você encontra as informações do projeto atual.'
            }
          },
          {
            element: '.tour-imprimir-projeto',
            popover: {
              title: 'Imprimir projeto',
              description: 'Use este botão para gerar um PDF com todos os componentes do projeto.'
            }
          },
          {
            element: '.tour-design-data',
            popover: {
              title: 'Design Data',
              description: 'Configure os parâmetros gerais que serão usados pelos componentes.'
            }
          },
          {
            element: '.tour-adicionar-bloco',
            popover: {
              title: 'Adicione um componente',
              description: 'Crie um bloco para selecionar uma calculadora e adicionar um componente.'
            }
          },
          {
            element: '.tour-blocos-componentes',
            popover: {
              title: 'Componentes em blocos',
              description: 'Os componentes aparecem em blocos. Clique na imagem de um componente para abrir a calculadora correspondente.'
            }
          },
          {
            element: '.tour-componentes-lista',
            popover: {
              title: 'Lista de componentes',
              description: 'Nesta lista você pode consultar os componentes criados e usar Abrir para acessar uma calculadora.'
            }
          }
        ],
        calculadora: [
          {
            element: '.calculadora-resumo',
            popover: {
              title: 'Dados do componente',
              description: 'Aqui você confere o nome, o tipo de componente, a calculadora correspondente e a ilustração.'
            }
          },
          {
            element: '.calculadora-impressao',
            popover: {
              title: 'Imprimir calculadora',
              description: 'Use o botão Imprimir para gerar um PDF com os dados preenchidos do cálculo.'
            }
          },
          {
            element: '.calculadora-impressao + .row',
            popover: {
              title: 'Botões de ação',
              description: 'Depois de Calcular, clique em Salvar para guardar os dados.'
            }
          },
          {
            element: '#container .tab-pane form',
            popover: {
              title: 'Preencha os parâmetros',
              description: 'Informe os parâmetros solicitados pela calculadora e confira as unidades antes de calcular.'
            }
          }
        ]
      };

      this.executarTour(stepsPorContexto[contexto] || [], contexto);
    },

    iniciarTourDaRotaAtual(path) {
      if (path.startsWith('/app/project/')) {
        this.iniciarTourContextual('componentes');
      } else if (path.startsWith('/app/calculator-')) {
        this.iniciarTourContextual('calculadora');
      }
    },

    executarTour(steps, contexto) {
      const stepsDisponiveis = steps.filter((step) => document.querySelector(step.element));
      if (!stepsDisponiveis.length) return;

      const tour = driver({
        showProgress: true,
        progressText: '{{current}} de {{total}}',
        animate: true,
        allowClose: true,
        disableActiveInteraction: true,
        overlayClickBehavior: 'none',
        nextBtnText: 'Próximo',
        prevBtnText: 'Voltar',
        doneBtnText: 'Concluir',
        steps: stepsDisponiveis,
        onDestroyed: () => {
          localStorage.setItem(this.chaveTour(contexto), 'true');
        }
      });

      tour.drive();
    }
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  }
};
</script>

<style scoped>
.tour-trigger {
  background-color: #0056ff;
  border: 1px solid #0056ff;
  border-radius: 6px;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  font-weight: 700;
  align-items: center;
  gap: 8px;
  width: calc(100% - 36px);
  margin: 12px auto 16px;
  justify-content: center;
}

.tour-trigger:hover {
  background-color: #0044cc;
  border-color: #0044cc;
  color: #ffffff;
}
</style>

<style>
.driver-popover-next-btn,
.driver-popover-prev-btn {
  border: 1px solid #0056ff !important;
  border-radius: 6px !important;
  background-color: #0056ff !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  text-shadow: none !important;
}

.driver-popover-next-btn:hover,
.driver-popover-prev-btn:hover {
  background-color: #0046cc !important;
  color: #ffffff !important;
}
</style>